/**
 * Talking to the native shell when the console is embedded in the mobile app.
 *
 * The mobile app hosts this console in a WebView so staff get the whole product
 * on a phone without a second frontend being written and maintained. Two things
 * cross that boundary:
 *
 *   - IN: a Firebase custom token, injected on `window.__DECODA_NATIVE__`
 *     before any page script runs, so the console can sign itself in rather
 *     than showing a login screen to someone already signed in natively.
 *   - OUT: call requests. A browser can only hand a number to `tel:`; the
 *     native app can place it through CallKit, so it rings like a real call,
 *     appears in the system call log, and survives backgrounding.
 *
 * Everything here is feature-detected. In an ordinary browser `isNativeHost()`
 * is false and callers fall back to existing behaviour, so the web app is
 * unaffected.
 */

export interface NativeHost {
  platform: "ios" | "android";
  customToken?: string;
  selectedTenant?: string | null;
}

interface NativeWindow extends Window {
  __DECODA_NATIVE__?: NativeHost;
  ReactNativeWebView?: { postMessage(data: string): void };
}

function nativeWindow(): NativeWindow | null {
  return typeof window === "undefined" ? null : (window as NativeWindow);
}

/** Whether this console is running inside the native mobile shell. */
export function isNativeHost(): boolean {
  const w = nativeWindow();
  return Boolean(w?.ReactNativeWebView && w.__DECODA_NATIVE__);
}

/** The handoff payload the native shell injected, if any. */
export function nativeHost(): NativeHost | null {
  return nativeWindow()?.__DECODA_NATIVE__ ?? null;
}

/**
 * Consume the injected custom token.
 *
 * Deleted as it is read so a single token is never exchanged twice and does not
 * linger on `window` for any later script to find.
 */
export function takeNativeCustomToken(): string | null {
  const w = nativeWindow();
  const token = w?.__DECODA_NATIVE__?.customToken;
  if (!w?.__DECODA_NATIVE__ || !token) return null;
  delete w.__DECODA_NATIVE__.customToken;
  return token;
}

export interface NativeCallRequest {
  /** E.164 destination. The native side re-validates; do not rely on this alone. */
  e164: string;
  patientId?: string;
  displayName?: string;
}

/**
 * Ask the native shell to place a call.
 *
 * Returns false when not running natively, so the caller can fall back to
 * whatever the web app does today:
 *
 *   if (!requestNativeCall({ e164, patientId })) {
 *     // existing web calling path
 *   }
 */
export function requestNativeCall(req: NativeCallRequest): boolean {
  const w = nativeWindow();
  if (!w?.ReactNativeWebView || !w.__DECODA_NATIVE__) return false;

  w.ReactNativeWebView.postMessage(
    JSON.stringify({
      type: "call",
      e164: req.e164,
      ...(req.patientId ? { patientId: req.patientId } : {}),
      ...(req.displayName ? { displayName: req.displayName } : {}),
    }),
  );
  return true;
}

/** Tell the native shell the console has finished booting. */
export function signalNativeReady(): void {
  const w = nativeWindow();
  if (!w?.ReactNativeWebView || !w.__DECODA_NATIVE__) return;
  w.ReactNativeWebView.postMessage(JSON.stringify({ type: "ready" }));
}

/**
 * Tell the app the user signed out, so it ends its own session too. Without
 * this the next WebView load signs them straight back in from the app's
 * session, and the phone keeps ringing for their calls. No-op in a browser.
 */
export function signalNativeSignOut(): void {
  const w = nativeWindow();
  if (!w?.ReactNativeWebView || !w.__DECODA_NATIVE__) return;
  w.ReactNativeWebView.postMessage(JSON.stringify({ type: "signOut" }));
}

/** Event the native shell dispatches to open a console page (see NativeAuthBridge). */
export const NATIVE_NAVIGATE_EVENT = "decoda:navigate";

/**
 * The path a native navigate event asks for, or null if it isn't a same-site
 * path. The event is dispatched by the app into the page, but anything on the
 * page could dispatch one too, so this refuses absolute and protocol-relative
 * URLs rather than trusting the sender.
 */
export function nativeNavigationPath(event: Event): string | null {
  const detail = (event as CustomEvent<{ path?: unknown }>).detail;
  const path = detail?.path;
  if (typeof path !== "string") return null;
  if (!path.startsWith("/") || path.startsWith("//") || path.includes("\\")) {
    return null;
  }
  return path;
}
